import { useState } from "react";
import * as React from "react";
import { Editor } from "slate";
import { ReactEditor, useSlate } from "slate-react";

import { Warning } from "@livechat/design-system-icons";
import { DesignToken, Icon, SpacingToken } from "@livechat/design-system-react-components";

import { IconsEnum } from "components/icon";
import Flex from "components/layout/Flex";
import Text from "components/layout/Text";
import { ToolbarIcon } from "components/toolbar";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { useTextEnhancers } from "hooks/use-text-enhancers";
import { ITextEnhancementsActionTypes, TextEnhancement } from "store/entities/enhancements/interfaces";
import { withVar } from "themes/helpers";

import { StyledIconsWrapper, StyledSecondaryToolbarContainer } from "../styles";

export interface IAiTextEnhancementsToolbarProps {
  mobile: boolean;
  privateMode: boolean;
  disabled: boolean;
}

const MAX_TEXT_TO_ENHANCE = 1024;

const AiTextEnhancementsToolbar: React.FC<IAiTextEnhancementsToolbarProps> = ({ mobile, privateMode, disabled }) => {
  const editor = useSlate();
  const [showTip, setShowTip] = useState(false);
  const { mutate, isLoading } = useTextEnhancers((text: string) => {
    Editor.insertText(editor, text);
  });

  const { selection } = editor;
  const selectedText = selection && Editor.string(editor, selection);
  const isEmptySelection = selectedText?.length === 0 || !ReactEditor.isFocused(editor);
  // @ts-expect-error TS(18048)
  const selectedTextTooLong = selectedText?.length >= MAX_TEXT_TO_ENHANCE;
  const shouldDisableControls = disabled || isLoading || isEmptySelection || selectedTextTooLong;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleTextEnhancement = (enhancementType: ITextEnhancementsActionTypes) => {
    // @ts-expect-error TS(2322)
    mutate({ input: selectedText, action: enhancementType });
    trackEngagement("AI Text Enhancement: Enhancement used", EventPlace.RichTextEditor, { type: enhancementType });
  };

  return (
    <StyledSecondaryToolbarContainer>
      <StyledIconsWrapper>
        <ToolbarIcon
          testSelector="ai-expand-text"
          icon={IconsEnum.ExpandText}
          active={false}
          disabled={shouldDisableControls}
          tooltip="Expand text"
          privateMode={privateMode}
          mobile={mobile}
          setShowTip={setShowTip}
          onMouseDown={() => handleTextEnhancement(TextEnhancement.MORE_ELABORATE)}
        />
        <ToolbarIcon
          testSelector="ai-more-causal"
          icon={IconsEnum.MoreCasual}
          active={false}
          disabled={shouldDisableControls}
          tooltip="Make more casual"
          privateMode={privateMode}
          mobile={mobile}
          setShowTip={setShowTip}
          onMouseDown={() => handleTextEnhancement(TextEnhancement.MORE_CASUAL)}
        />
        <ToolbarIcon
          testSelector="ai-more-polite"
          icon={IconsEnum.MorePolite}
          active={false}
          disabled={shouldDisableControls}
          tooltip="Make more polite"
          privateMode={privateMode}
          mobile={mobile}
          setShowTip={setShowTip}
          onMouseDown={() => handleTextEnhancement(TextEnhancement.MORE_POLITE)}
        />
        <ToolbarIcon
          testSelector="ai-more-official"
          icon={IconsEnum.MoreOfficial}
          active={false}
          disabled={shouldDisableControls}
          tooltip="Make more formal"
          privateMode={privateMode}
          mobile={mobile}
          setShowTip={setShowTip}
          onMouseDown={() => handleTextEnhancement(TextEnhancement.MORE_FORMAL)}
        />
        <ToolbarIcon
          testSelector="ai-grammar"
          icon={IconsEnum.Grammar}
          active={false}
          disabled={shouldDisableControls}
          tooltip="Correct grammar"
          privateMode={privateMode}
          mobile={mobile}
          setShowTip={setShowTip}
          onMouseDown={() => handleTextEnhancement(TextEnhancement.CORRECT_GRAMMAR)}
        />
        {(showTip || mobile) && isEmptySelection && !isLoading && (
          <Text
            css={
              mobile
                ? {
                    paddingBottom: withVar(SpacingToken.Spacing1),
                    paddingTop: withVar(SpacingToken.Spacing3),
                  }
                : undefined
            }
            color={`var(${DesignToken.ContentBasicDisabled})`}
          >
            Note: Select the text to enhance it.
          </Text>
        )}
        {selectedTextTooLong && !mobile && (
          <Flex alignItems="center" gap="4px">
            <Icon source={Warning} customColor={`var(${DesignToken.ContentBasicWarning})`} />
            <Text color={`var(${DesignToken.ContentBasicDisabled})`}>Select a max. of 1024 characters</Text>
          </Flex>
        )}
      </StyledIconsWrapper>
    </StyledSecondaryToolbarContainer>
  );
};

export default AiTextEnhancementsToolbar;
